import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../lib/api";
import { getPaymentStatus } from "../lib/api/index";
import { logger } from "../lib/logger";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;          // 60 × 2s = 120s
const ESCAPE_LINK_AFTER = 5;           // show manual continue link after attempt 5 (~10s)

// Status-driven copy extracted to flat lookups (removes nested ternaries)
const HEADLINE_BY_STATUS = {
  ready:      "Audit unlocked.",
  generating: "Generating your audit…",
  timeout:    "Payment confirmed.",
};
const HEADLINE_DEFAULT = "Verifying your payment…";

const BODY_BY_STATUS = {
  generating: "Your 13-section report is being compiled. This usually takes 30–90 seconds.",
  timeout:    "Your report is taking longer than expected to generate. Continue to your report — it will appear there as soon as it's ready.",
};
const BODY_DEFAULT = "Confirming payment with Stripe and unlocking your report. Please don't close this window.";

/**
 * Post-Stripe-redirect landing page.
 *
 * Polling rules (per CEO directive):
 *   - useEffect deps: [sessionId] only — start once on mount, clean up on unmount.
 *   - setInterval at 2s cadence, capped at 60 attempts.
 *   - Functional setState for attempt counter (no stale closures).
 *   - When paid && has_report → clear interval, fire `checkout_completed`,
 *     navigate to /audit/report/:sessionId.
 *   - After attempt 5, render an ALWAYS-VISIBLE escape link to the report — the
 *     user is never stranded.
 *   - When 60 attempts exhaust, show an explicit "view your report" button.
 */
export default function PaidSuccess() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("verifying"); // "verifying" | "generating" | "ready" | "timeout"
  const [showEscape, setShowEscape] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const navigatedRef = useRef(false);
  const completedTrackedRef = useRef(false);

  useEffect(() => {
    if (!completedTrackedRef.current) {
      completedTrackedRef.current = true;
      trackEvent("checkout_completed", `/audit/paid/success/${sessionId}`, {}, sessionId);
    }

    let cancelled = false;
    let intervalId = null;

    const tick = async () => {
      if (cancelled || navigatedRef.current) return;
      // Increment attempt counter with FUNCTIONAL setState to avoid stale closures.
      let currentAttempt = 0;
      setAttempt((a) => {
        currentAttempt = a + 1;
        return currentAttempt;
      });

      // Escape link becomes visible after ESCAPE_LINK_AFTER attempts (~10s).
      if (currentAttempt >= ESCAPE_LINK_AFTER) {
        setShowEscape(true);
      }

      // Stop polling once we've hit the cap. The escape link is already visible.
      if (currentAttempt > MAX_POLL_ATTEMPTS) {
        if (intervalId) clearInterval(intervalId);
        setStatus("timeout");
        setTimedOut(true);
        return;
      }

      try {
        const data = (await getPaymentStatus(sessionId)) || {};
        if (cancelled || navigatedRef.current) return;
        if (data.paid === true && data.has_report === true) {
          navigatedRef.current = true;
          setStatus("ready");
          if (intervalId) clearInterval(intervalId);
          // Hard navigate to the report. `replace: true` so the back button
          // doesn't bounce the user back to the success page.
          navigate(`/audit/report/${sessionId}`, { replace: true });
          return;
        }
        // Show a clearer label once we know payment landed but report still cooking
        if (data.paid === true) {
          setStatus("generating");
        } else {
          setStatus("verifying");
        }
      } catch (err) {
        logger.error("payment_status_poll_failed", {
          sessionId,
          attempt: currentAttempt,
          err: err?.response?.status || err?.message,
        });
        // Don't bail on transient errors — keep polling.
      }
    };

    // Fire the first poll immediately so the user doesn't wait 2s on a blank page.
    tick();
    intervalId = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [sessionId, navigate]);

  const onManualContinue = useCallback(() => {
    navigatedRef.current = true;
    navigate(`/audit/report/${sessionId}`, { replace: true });
  }, [navigate, sessionId]);

  return (
    <div className="sf-container" style={{ padding: "80px 24px", maxWidth: 640 }} data-testid="paid-success-page">
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
        <img src="/systemforge-logo.png" alt="" style={{ width: 56, height: 56, opacity: 0.9 }} />
      </div>
      <div className="sf-eyebrow" style={{ textAlign: "center", marginBottom: 8 }}>SystemForge AI</div>
      <h1
        className="sf-display"
        style={{ fontSize: 32, textAlign: "center", marginBottom: 16, lineHeight: 1.15 }}
        data-testid="paid-success-headline"
      >
        {HEADLINE_BY_STATUS[status] || HEADLINE_DEFAULT}
      </h1>
      <p className="sf-muted-text" style={{ textAlign: "center", marginBottom: 24 }} data-testid="paid-success-status-text">
        {BODY_BY_STATUS[status] || BODY_DEFAULT}
      </p>

      <div className="sf-mono sf-muted-text" style={{ textAlign: "center", fontSize: 12 }} data-testid="paid-success-attempt">
        Attempt {attempt} / {MAX_POLL_ATTEMPTS}
      </div>

      {showEscape && !timedOut && (
        <div style={{ textAlign: "center", marginTop: 28 }} data-testid="paid-success-escape">
          <Link
            to={`/audit/report/${sessionId}`}
            data-testid="paid-success-continue-link"
            className="sf-btn-secondary"
            style={{ display: "inline-block" }}
          >
            Continue to your report →
          </Link>
          <div className="sf-mono sf-muted-text" style={{ fontSize: 11, marginTop: 8 }}>
            Don't want to wait? The report opens as soon as it's ready.
          </div>
        </div>
      )}

      {timedOut && (
        <div className="sf-card" style={{ marginTop: 28 }} data-testid="paid-success-timeout">
          <div className="sf-eyebrow" style={{ color: "var(--sf-gold)" }}>Take me to my report</div>
          <p style={{ marginTop: 10 }}>
            Payment is confirmed. Your full report will appear on the next page the moment generation completes.
          </p>
          <button
            type="button"
            onClick={onManualContinue}
            className="sf-btn"
            data-testid="paid-success-manual-continue"
          >
            View my report
          </button>
        </div>
      )}
    </div>
  );
}
